"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.HIT_DETECT_RESOLUTION = void 0;
exports.createHitDetectionImageData = createHitDetectionImageData;
exports.hitDetect = hitDetect;

var _Immediate = _interopRequireDefault(require("./Immediate.js"));

var _GeometryType = _interopRequireDefault(require("../../geom/GeometryType.js"));

var _IconAnchorUnits = _interopRequireDefault(require("../../style/IconAnchorUnits.js"));

var _style = require("../../style.js");

var _math = require("../../math.js");

var _dom = require("../../dom.js");

var _extent = require("../../extent.js");

var _array = require("../../array.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/render/canvas/hitdetect
 */
var HIT_DETECT_RESOLUTION = 0.5;
/**
 * @param {import("../../size.js").Size} size Canvas size in css pixels.
 * @param {Array<import("../../transform.js").Transform>} transforms Transforms
 * for rendering features to all worlds of the viewport, from coordinates to css
 * pixels.
 * @param {Array<import("../../Feature.js").FeatureLike>} features
 * Features to consider for hit detection.
 * @param {import("../../style/Style.js").StyleFunction|undefined} styleFunction
 * Layer style function.
 * @param {import("../../extent.js").Extent} extent Extent.
 * @param {number} resolution Resolution.
 * @param {number} rotation Rotation.
 * @return {ImageData} Hit detection image data.
 */

exports.HIT_DETECT_RESOLUTION = HIT_DETECT_RESOLUTION;

function createHitDetectionImageData(size, transforms, features, styleFunction, extent, resolution, rotation) {
  var width = size[0] * HIT_DETECT_RESOLUTION;
  var height = size[1] * HIT_DETECT_RESOLUTION;
  var context = (0, _dom.createCanvasContext2D)(width, height);
  context.imageSmoothingEnabled = false;
  var canvas = context.canvas;
  var renderer = new _Immediate["default"](context, HIT_DETECT_RESOLUTION, extent, null, rotation);
  var featureCount = features.length; // Stretch hit detection index to use the whole available color range

  var indexFactor = Math.floor((256 * 256 * 256 - 1) / featureCount);
  var featuresByZIndex = {};

  for (var i = 1; i <= featureCount; ++i) {
    var feature = features[i - 1];
    var featureStyleFunction = feature.getStyleFunction() || styleFunction;

    if (!styleFunction) {
      continue;
    }

    var styles = featureStyleFunction(feature, resolution);

    if (!styles) {
      continue;
    }

    if (!Array.isArray(styles)) {
      styles = [styles];
    }

    var index = i * indexFactor;
    var color = '#' + ('000000' + index.toString(16)).slice(-6);

    for (var j = 0, jj = styles.length; j < jj; ++j) {
      var originalStyle = styles[j];
      var geometry = originalStyle.getGeometryFunction()(feature);

      if (!geometry || !(0, _extent.intersects)(extent, geometry.getExtent())) {
        continue;
      }

      var style = originalStyle.clone();
      var fill = style.getFill();

      if (fill) {
        fill.setColor(color);
      }

      var stroke = style.getStroke();

      if (stroke) {
        stroke.setColor(color);
        stroke.setLineDash(null);
      }

      style.setText(undefined);
      var image = originalStyle.getImage();

      if (image && image.getOpacity() !== 0) {
        var imgSize = image.getImageSize();

        if (!imgSize) {
          continue;
        }

        var imgContext = (0, _dom.createCanvasContext2D)(imgSize[0], imgSize[1], undefined, {
          alpha: false
        });
        var img = imgContext.canvas;
        imgContext.fillStyle = color;
        imgContext.fillRect(0, 0, img.width, img.height);
        style.setImage(new _style.Icon({
          img: img,
          imgSize: imgSize,
          anchor: image.getAnchor(),
          anchorXUnits: _IconAnchorUnits["default"].PIXELS,
          anchorYUnits: _IconAnchorUnits["default"].PIXELS,
          offset: image.getOrigin(),
          opacity: 1,
          size: image.getSize(),
          scale: image.getScale(),
          rotation: image.getRotation(),
          rotateWithView: image.getRotateWithView()
        }));
      }

      var zIndex = style.getZIndex() || 0;
      var byGeometryType = featuresByZIndex[zIndex];

      if (!byGeometryType) {
        byGeometryType = {};
        featuresByZIndex[zIndex] = byGeometryType;
        byGeometryType[_GeometryType["default"].POLYGON] = [];
        byGeometryType[_GeometryType["default"].CIRCLE] = [];
        byGeometryType[_GeometryType["default"].LINE_STRING] = [];
        byGeometryType[_GeometryType["default"].POINT] = [];
      }

      byGeometryType[geometry.getType().replace('Multi', '')].push(geometry, style);
    }
  }

  var zIndexKeys = Object.keys(featuresByZIndex).map(Number).sort(_array.numberSafeCompareFunction);

  for (var i = 0, ii = zIndexKeys.length; i < ii; ++i) {
    var byGeometryType = featuresByZIndex[zIndexKeys[i]];

    for (var type in byGeometryType) {
      var geomAndStyle = byGeometryType[type];

      for (var j = 0, jj = geomAndStyle.length; j < jj; j += 2) {
        renderer.setStyle(geomAndStyle[j + 1]);

        for (var k = 0, kk = transforms.length; k < kk; ++k) {
          renderer.setTransform(transforms[k]);
          renderer.drawGeometry(geomAndStyle[j]);
        }
      }
    }
  }

  return context.getImageData(0, 0, canvas.width, canvas.height);
}
/**
 * @param {import("../../pixel").Pixel} pixel Pixel coordinate on the hit
 * detection canvas in css pixels.
 * @param {Array<import("../../Feature").FeatureLike>} features Features. Has to
 * match the `features` array that was passed to `createHitDetectionImageData()`.
 * @param {ImageData} imageData Hit detection image data generated by
 * `createHitDetectionImageData()`.
 * @return {Array<import("../../Feature").FeatureLike>} features Features.
 */


function hitDetect(pixel, features, imageData) {
  var resultFeatures = [];

  if (imageData) {
    var x = Math.floor(Math.round(pixel[0]) * HIT_DETECT_RESOLUTION);
    var y = Math.floor(Math.round(pixel[1]) * HIT_DETECT_RESOLUTION); // The pixel coordinate is clamped down to the hit-detect canvas' size to account
    // for browsers returning coordinates slightly larger than the actual canvas size
    // due to a non-integer pixel ratio.

    var index = ((0, _math.clamp)(x, 0, imageData.width - 1) + (0, _math.clamp)(y, 0, imageData.height - 1) * imageData.width) * 4;
    var r = imageData.data[index];
    var g = imageData.data[index + 1];
    var b = imageData.data[index + 2];
    var i = b + 256 * (g + 256 * r);
    var indexFactor = Math.floor((256 * 256 * 256 - 1) / features.length);

    if (i && i % indexFactor === 0) {
      resultFeatures.push(features[i / indexFactor - 1]);
    }
  }

  return resultFeatures;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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